import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Dropdown, Field, Option, makeStyles, tokens } from '@fluentui/react-components';
import { Filter16Filled, Filter16Regular } from '@fluentui/react-icons';
import { EntityColumn } from './types';
import {
  FilterType, NO_VALUE_TOKEN, OPERATORS, PARSEABLE_ONLY_OPS, applyFilterValue, applyOperatorChange, booleanLookupOptions,
  clearColumnFilters, currentOp as resolveCurrentOp, filterPrefix, renderValueInput
} from './columnFilterOps';
import { readLookupSelection, writeLookupSelection, type LookupSelection } from './picklistSelection';
import { SearchableMultiSelect } from './SearchableMultiSelect';
import { acquireFilterSurface, releaseFilterSurface } from './filterSurfaceRegistry';
import { isInsideOwnedSurface } from './surfaceOwnership';
import i18n from '../../i18n';

// Unified list-filters program (spec 2026-07-22) — Dataverse-style column-header filtering,
// rewritten from CRM's Tailwind source into Fluent `makeStyles` for supervision. Clicking a
// column header's filter trigger opens a disclosure panel with THAT column's operator + value
// controls, reusing OPERATORS/renderValueInput from columnFilterOps (never duplicate operator
// logic). A plain disclosure button + panel (NOT a Fluent Popover/Menu) so nested Dropdown option
// portals aren't treated as outside-clicks.
//
// HAZARD (spec §"Render-loop hazard"): a controlled Fluent v9 Dropdown with an aria-label/title
// triggers a synchronous infinite render loop that hangs the page and wedges vitest. The operator
// Dropdown is labelled via a visually-hidden <Field> label — NEVER aria-label.
//
// ── Fleet filter contract §6.6 (v1.3), rules I1–I5 ──────────────────────────────────────────────
//
// I1  One surface at a time — delegated to `filterSurfaceRegistry`, because this is a property of
//     the PAGE and one instance per column cannot see its siblings.
// I2  Escape dismisses and returns focus to the trigger. Under I5 it ALSO discards (§9.5 amends I2).
// I3  Outside-click dismisses, with "outside" scoped by OWNERSHIP — see `surfaceOwnership`, and
//     note that the naive `panelRef.contains(target)` is the implementation the contract RULES OUT.
// I4  No overlap-stacking; I1 gives this for filter surfaces by construction.
// I5  🔑 THE PANEL STAGES. Edits are local and inert; **Apply** is the only thing that commits.
//
// ⚠ WHAT "ONE FETCH" MEANS IN THIS REPO. The contract counts fetches. Supervision's list data path
// is fetch-all-then-filter (`useListView` → `applyClientFilters` in a `useMemo`), so a filter change
// fires no request at all — the commit boundary is the URL write, i.e. exactly one `onChange` call.
// That is the honest instrument here and it catches precisely the defect I5 removes: three staged
// fields committed in three writes are three history replaces and three re-filters. Conformance
// cases C34/C35/C37/C39 are asserted as onChange CALL COUNTS, not as "the list updated" — which is
// also true under the defect.

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'inline-flex',
  },
  panel: {
    position: 'absolute',
    left: 0,
    top: '100%',
    zIndex: 20,
    marginTop: '4px',
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingVerticalM,
    boxShadow: tokens.shadow16,
    textTransform: 'none',
    fontWeight: tokens.fontWeightRegular,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
});

export interface ColumnHeaderFilterProps<TItem> {
  column: EntityColumn<TItem>;
  /** The full flat filter record (e.g. `list.filters`) — the APPLIED state. */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /**
   * Increment to open this column's panel from elsewhere — the column header menu's "Filter by…"
   * (contract v2 §13.1). Opening still goes through the one-surface registry (I1), so the menu that
   * asked closes as this opens.
   */
  openRequest?: number;
  /**
   * C86 facets (§16.3): per-option row counts for a lookup column, with "(has no value)" under NO_VALUE_TOKEN
   * (`useListView().facetCountsFor`). A function, called only while the panel is open. Omitted: no counts are shown,
   * since "not computed" and "zero" are different facts.
   */
  getFacetCounts?: () => Record<string, number>;
  /** A lookup column's options when the column cannot declare them (`useListView().lookupOptionsFor`). */
  getLookupOptions?: () => { value: string; label: string }[];
}

function sameRecord(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => b[k] === a[k]);
}

export function ColumnHeaderFilter<TItem>({ column, value, onChange, openRequest, getFacetCounts, getLookupOptions }: ColumnHeaderFilterProps<TItem>) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [opChoice, setOpChoice] = useState<string | undefined>(undefined);
  // I5: the DRAFT. Seeded from the applied state every time the panel opens, which is what makes
  // re-open show last-applied rather than the abandoned edits (C36's re-open clause — the clause
  // most likely to be dropped as redundant, and the only one that catches staged state parked in a
  // component that survives the close).
  const [staged, setStaged] = useState<Record<string, string>>(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Stable identity: this is what the registry holds and calls back to evict us (I1).
  const close = useCallback(() => {
    setOpen(false);
    setOpChoice(undefined);
  }, []);

  // Our own dismissal also gives up the slot. Eviction by the registry does not need to — it
  // clears the slot before calling us.
  const dismiss = useCallback(() => {
    close();
    releaseFilterSurface(close);
  }, [close]);

  useEffect(() => () => releaseFilterSurface(close), [close]);

  // Open on a NEW request only — re-renders with the same counter, or a changed `value`, must not
  // re-open a panel the user just dismissed.
  const lastOpenRequest = useRef(openRequest ?? 0);
  useEffect(() => {
    if (!openRequest || openRequest === lastOpenRequest.current) return;
    lastOpenRequest.current = openRequest;
    setStaged(value);
    setOpChoice(undefined);
    setOpen(true);
    acquireFilterSurface(close);
  }, [openRequest, value, close]);

  // I3 — dismissal scoped by ownership, not containment.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (isInsideOwnedSurface(e.target as Node | null, [triggerRef.current, panelRef.current])) return;
      dismiss();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, dismiss]);

  if (!column.filterType) return null;

  const prefix = filterPrefix(column.key);
  // The funnel glyph is a readout of APPLIED state, never of the draft (I5: "no change to the
  // active-filter chips" until Apply — the same clause, applied to the trigger's own indicator).
  const hasActive = Object.keys(value).some((k) => k.startsWith(prefix));
  const op = resolveCurrentOp(column, staged, opChoice);
  // The offered menu, plus a parseable-only op ONLY while the URL already carries it (contract v2 D2).
  const ops = [
    ...OPERATORS[column.filterType as FilterType],
    ...(PARSEABLE_ONLY_OPS[column.filterType as FilterType] ?? []).filter((o) => o.op === op),
  ];
  const opLabel = ops.find((o) => o.op === op)?.label ?? op;
  const v = staged[`${prefix}${op}`] ?? '';

  // C86 (fleet filter contract v3 §16.8, BINDING): a lookup column (picklist or boolean) filters through ONE searchable
  // multi-select combobox, never an operator dropdown over a value picker. The staged {op, value} is read into the
  // combobox's model and written back from it (picklistSelection, copied from core), so Apply/Escape/Clear (I5) and the
  // wire ops (`in` / `notIn` + the no-value token) are unchanged: a URL or saved view carries it as before.
  const isLookup = column.filterType === 'picklist' || column.filterType === 'boolean';
  const lookupOptions = column.filterType === 'boolean'
    ? booleanLookupOptions()
    : column.filterOptions ?? (isLookup && open ? getLookupOptions?.() : undefined) ?? [];
  const selection: LookupSelection = isLookup ? readLookupSelection(op, v) : { excluding: false, codes: [], includeNoValue: false };
  const facetCounts = isLookup && open ? getFacetCounts?.() : undefined;

  function commitSelection(next: LookupSelection) {
    const { op: newOp, value: newVal } = writeLookupSelection(next);
    const nextStaged = { ...staged };
    clearColumnFilters(nextStaged, column.key);
    if (newVal !== '') nextStaged[`${prefix}${newOp}`] = newVal;
    setStaged(nextStaged);
    setOpChoice(newOp);
  }

  function openPanel() {
    setStaged(value);
    setOpChoice(undefined);
    setOpen(true);
    acquireFilterSurface(close);
  }

  function toggle() {
    if (open) {
      dismiss();
      triggerRef.current?.focus();
    } else {
      openPanel();
    }
  }

  function selectOp(newOp: string) {
    if (newOp === op) return;
    setOpChoice(newOp);
    const { next, shouldEmit } = applyOperatorChange(column, op, newOp, staged);
    if (shouldEmit) setStaged(next);
  }

  function setFilterValue(val: string) {
    setStaged(applyFilterValue(column, op, val, staged));
  }

  /**
   * I5 lean (§6.6, flagged in the contract as a lean rather than a verbatim ruling): Clear is an
   * EDIT, so it stages. Clear-then-Escape leaves the filter applied; Clear-then-Apply removes it
   * (C38). Immediate clearing is already served outside the popover by the active-filter chips,
   * which commit on click because there is nothing staged for them to stage.
   */
  function stageClear() {
    const next = { ...staged };
    for (const k of Object.keys(next)) if (k.startsWith(prefix)) delete next[k];
    setStaged(next);
    setOpChoice(undefined);
  }

  /**
   * The only commit. Rebuilt onto the CURRENT applied record rather than shipping the draft
   * wholesale, so this column's keys are the only ones this write can move — a stale draft cannot
   * resurrect a filter cleared elsewhere (via a chip) while the panel sat open.
   */
  function apply() {
    const next = { ...value };
    for (const k of Object.keys(next)) if (k.startsWith(prefix)) delete next[k];
    for (const [k, val] of Object.entries(staged)) if (k.startsWith(prefix)) next[k] = val;
    // C37: Apply with nothing staged is not a refetch. An unconditional emit here passes C35 and
    // quietly doubles the traffic of every popover visit.
    if (!sameRecord(next, value)) onChange(next);
    dismiss();
    triggerRef.current?.focus();
  }

  return (
    <span
      className={styles.root}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // I2 + I5: dismiss AND discard. `staged` is reseeded from `value` on the next open, so
          // there is nothing further to reset here.
          dismiss();
          triggerRef.current?.focus();
        }
      }}
    >
      <Button
        ref={triggerRef}
        appearance="subtle"
        size="small"
        icon={hasActive ? <Filter16Filled /> : <Filter16Regular />}
        aria-label={i18n.t('filters.filterColumn', { column: column.label })}
        aria-pressed={hasActive}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggle}
      />
      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label={i18n.t('filters.panel', { column: column.label })}
          className={styles.panel}
        >
          {isLookup ? (
            <SearchableMultiSelect
              label={column.label}
              options={lookupOptions}
              selected={selection.codes}
              onChange={(codes) => commitSelection({ ...selection, codes })}
              facetCounts={facetCounts}
              noValueOption={{
                label: i18n.t('filters.lookup.hasNoValue'),
                checked: selection.includeNoValue,
                count: facetCounts?.[NO_VALUE_TOKEN],
                onChange: (checked) => commitSelection({ ...selection, includeNoValue: checked }),
              }}
              excludeToggle={{
                label: i18n.t('filters.lookup.exclude'),
                checked: selection.excluding,
                onChange: (checked) => commitSelection({ ...selection, excluding: checked }),
              }}
              searchPlaceholder={i18n.t('filters.lookup.searchPlaceholder')}
              labels={{
                selectAll: i18n.t('filters.lookup.selectAll'),
                clear: i18n.t('filters.lookup.clear'),
                noMatches: i18n.t('filters.lookup.noMatches'),
                valueSuffix: i18n.t('filters.lookup.valueSuffix'),
                searchSuffix: i18n.t('filters.lookup.searchSuffix'),
              }}
              onEnter={apply}
            />
          ) : (
            <>
              {/* No aria-label on the controlled Dropdown — Fluent v9 render-loop hang. */}
              <Field label={{ children: `${column.label} operator`, className: 'sr-only' }}>
                <Dropdown
                  size="small"
                  value={opLabel}
                  selectedOptions={[op]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue) selectOp(d.optionValue);
                  }}
                >
                  {ops.map((o) => (
                    <Option key={o.op} value={o.op}>
                      {o.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              {renderValueInput(column, op, v, setFilterValue)}
            </>
          )}
          <div className={styles.actions}>
            <Button size="small" appearance="subtle" onClick={stageClear}>
              {i18n.t('filters.clear')}
            </Button>
            <Button size="small" appearance="primary" onClick={apply}>
              {i18n.t('filters.apply')}
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  );
}
